// The in-memory twin of a member's own memory (RECALL-PEOPLE-SURFACES; services/api/src/routes/
// memory-people.ts and the member half of memory-governance.ts): Profile › Memory (what memory
// knows about me, fix, holds, "Remember my DMs"), Remember this, Off the record and "forget this"
// (preview, confirm token, 24-hour undo). The twin keeps each person's own lines in memory and
// answers like the API: a member only, a person's session only (the agency operator is refused),
// and, when the company's Recall is not connected, "Waiting on memory service" instead of an
// error. Nothing is shared between people.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";
import { uid } from "./fake-data";
import type { FakeRecallState } from "./fake-memory";

export const FAKE_MEMORY_PEOPLE_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "memoryMe.get",
  "memoryMe.act",
  "memoryRemember.post",
  "memoryOffRecord.get",
  "memoryOffRecord.set",
  "memoryForget.list",
  "memoryForget.step",
]);

const WAITING = "Waiting on memory service";
const DAY_MS = 86_400_000;
const TOKEN_MS = 15 * 60_000;

type Forget = OperationOutput<"memoryForget.list">["items"][number];
type Line = { type: string; title: string; text: string };
type Mine = {
  lines: Line[];
  dms: boolean;
  dmsMirrored: boolean;
  off: Map<string, string>;
  forgets: (Forget & { token: string | null; line: Line | null; target: string })[];
};

const SEED: Line[] = [
  {
    type: "Note",
    title: "How I like updates",
    text: "Short written briefs on Monday mornings; details only when I ask.",
  },
  { type: "Note", title: "Working hours", text: "Central time, 8 to 4. Calls before 10 are fine." },
  {
    type: "Preference",
    title: "Candidate calls",
    text: "Twenty minutes with the scorecard open; notes go on the candidate the same day.",
  },
];

export function createMemoryPeopleFake(deps: {
  now: () => Date;
  recall: FakeRecallState;
}): Partial<Record<RouteId, Handler>> {
  const people = new Map<string, Mine>();
  const live = deps.recall === "connected";
  const at = (ms = 0) => new Date(deps.now().getTime() + ms).toISOString();
  let n = 0;

  /** The caller as themself: a person's membership in this company, or the refusal. */
  function who(p: Principal | null): { key: string; id: string; mine: Mine } | Response {
    if (!p) return notFound();
    if (p.roles.every((r) => r === "client_viewer"))
      return problem(403, "forbidden", "Forbidden", "Members only");
    const id = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
    if (!id)
      return problem(403, "forbidden", "Forbidden", "Memory acts as you: sign in as yourself");
    const key = `${p.company.id}:${id}`;
    let mine = people.get(key);
    if (!mine) {
      mine = {
        lines: live ? SEED.map((l) => ({ ...l })) : [],
        dms: false,
        dmsMirrored: false,
        off: new Map(),
        forgets: [],
      };
      people.set(key, mine);
    }
    return { key, id, mine };
  }

  const home = (p: Principal) => uid(0x7f10, p.company.slug.length + 1);
  const target = (p: Principal, l: Line) => `${home(p)}/${l.type}/${l.title}`;

  const view = (p: Principal, m: Mine) => ({
    capabilities: { me: live, waiting_text: WAITING },
    about: {
      waiting: live ? null : WAITING,
      lines: m.lines.map((l) => ({ ...l, target: target(p, l) })),
    },
    holds: [],
    remember_dms: { on: m.dms, mirrored: m.dms && m.dmsMirrored },
  });

  const forgetView = (f: Mine["forgets"][number]): Forget => ({
    id: f.id,
    mode: f.mode,
    scope: f.scope,
    label: f.label,
    state: f.state,
    preview: f.preview,
    case_ref: f.case_ref,
    undo_until: f.state === "confirmed" ? f.undo_until : null,
    quarantine_until: null,
    created_at: f.created_at,
  });
  const offList = (m: Mine) => ({
    items: [...m.off].map(([channel_id, since]) => ({ channel_id, since })),
  });

  return {
    "memoryMe.get": ({ principal: p }) => {
      const w = who(p);
      return w instanceof Response ? w : json(view(p as Principal, w.mine));
    },
    "memoryMe.act": ({ principal: p, body }) => {
      const w = who(p);
      if (w instanceof Response) return w;
      const b = (body ?? {}) as {
        action?: string;
        type?: string;
        title?: string;
        text?: string;
        on?: unknown;
      };
      if (b.action === "dms") {
        if (typeof b.on !== "boolean") return validation("on is a boolean");
        w.mine.dms = b.on;
        w.mine.dmsMirrored = live;
        return json({ waiting: live ? null : WAITING, view: view(p as Principal, w.mine) });
      }
      if (b.action !== "fix") return validation("action: fix or dms");
      if (!live) return json({ waiting: WAITING, view: view(p as Principal, w.mine) });
      const line = w.mine.lines.find((l) => l.type === b.type && l.title === b.title);
      if (!line || typeof b.text !== "string") return notFound();
      line.text = b.text;
      return json({ waiting: null, view: view(p as Principal, w.mine) });
    },
    "memoryRemember.post": ({ principal: p, body }) => {
      const w = who(p);
      if (w instanceof Response) return w;
      const b = (body ?? {}) as { text?: unknown; title?: unknown };
      if (typeof b.text !== "string" || !b.text.trim()) return validation("text is required");
      const flat = b.text.replace(/\s+/g, " ").trim();
      const title =
        typeof b.title === "string" && b.title.trim()
          ? b.title.trim()
          : flat.length > 60
            ? `${flat.slice(0, 57)}...`
            : flat;
      if (!live) return json({ remembered: false, title, waiting: WAITING });
      const i = w.mine.lines.findIndex((l) => l.type === "Note" && l.title === title);
      const line = { type: "Note", title, text: b.text };
      if (i >= 0) w.mine.lines[i] = line;
      else w.mine.lines.push(line);
      return json({ remembered: true, title, waiting: null });
    },
    "memoryOffRecord.get": ({ principal: p }) => {
      const w = who(p);
      return w instanceof Response ? w : json(offList(w.mine));
    },
    "memoryOffRecord.set": ({ principal: p, body }) => {
      const w = who(p);
      if (w instanceof Response) return w;
      const b = (body ?? {}) as { channel_id?: unknown; on?: unknown };
      if (typeof b.channel_id !== "string" || typeof b.on !== "boolean")
        return validation("channel_id and on are required");
      if (b.on) w.mine.off.set(b.channel_id, w.mine.off.get(b.channel_id) ?? at());
      else w.mine.off.delete(b.channel_id);
      return json(offList(w.mine));
    },
    "memoryForget.list": ({ principal: p }) => {
      const w = who(p);
      if (w instanceof Response) return w;
      return json({
        items: w.mine.forgets
          .filter((f) =>
            ["waiting", "previewed", "blocked", "failed", "confirmed"].includes(f.state),
          )
          .map(forgetView),
        capabilities: { holds: false, retention: false, forget: live, waiting_text: WAITING },
      });
    },
    "memoryForget.step": ({ principal: p, body }) => {
      const w = who(p);
      if (w instanceof Response) return w;
      const b = (body ?? {}) as {
        step?: string;
        scope?: string;
        target?: string;
        request_id?: string;
        token?: string;
      };
      const reply = (f: Mine["forgets"][number], token: string | null, waiting: string | null) =>
        json({ request: forgetView(f), confirm_token: token, waiting });
      if (b.step === "preview") {
        if (b.scope !== "record")
          return problem(
            403,
            "forbidden",
            "Forbidden",
            "only an owner or an admin forgets more than their own records",
          );
        const m = /^([^/]+)\/([^/]+)\/(.+)$/.exec(b.target ?? "");
        if (!m) return validation("a record is <space id>/<type>/<title>");
        const line = w.mine.lines.find((l) => l.type === m[2] && l.title === m[3]);
        if (!line || m[1] !== home(p as Principal)) return notFound();
        n += 1;
        const token = `ft_${n}`;
        const f: Mine["forgets"][number] = {
          id: uid(0x7f20, n),
          mode: "forget",
          scope: "record",
          label: line.title,
          state: live ? "previewed" : "waiting",
          preview: live
            ? { count: 1, by_kind: { [line.type]: 1 }, token_expires_at: at(TOKEN_MS) }
            : null,
          case_ref: null,
          undo_until: null,
          quarantine_until: null,
          created_at: at(),
          token: live ? token : null,
          line,
          target: b.target as string,
        };
        w.mine.forgets.unshift(f);
        return reply(f, live ? token : null, live ? null : WAITING);
      }
      const f = w.mine.forgets.find((x) => x.id === b.request_id);
      if (!f) return notFound();
      if (b.step === "confirm") {
        if (f.state !== "previewed")
          return problem(409, "conflict", "Conflict", `this request is ${f.state}`);
        if (!f.token || b.token !== f.token) return validation("that is not this preview's token");
        f.state = "confirmed";
        f.token = null;
        f.undo_until = at(DAY_MS);
        w.mine.lines = w.mine.lines.filter((l) => l !== f.line);
        return reply(f, null, null);
      }
      if (b.step === "undo") {
        if (f.state !== "confirmed")
          return problem(409, "conflict", "Conflict", `this request is ${f.state}`);
        if (f.line) w.mine.lines.push(f.line);
        f.state = "undone";
        f.undo_until = null;
        return reply(f, null, null);
      }
      if (b.step === "dismiss") {
        if (f.state === "confirmed")
          return problem(409, "conflict", "Conflict", `this request is ${f.state}`);
        f.state = "expired";
        f.token = null;
        return reply(f, null, null);
      }
      return validation("step: preview, confirm, undo or dismiss");
    },
  };
}
